// Held items (PvP depth, season one; design note mythic-idle-design/trail-and-held-items.md): one per creature, in a
// slot of its own beside the Relic, and only in PvP. Pure rules shared by the server, the client, the bots and tests.
//
// - Where they count: the Arena and ranked, Element Halls, friendly duels and training, practice runs, tournaments
//   and Trail trainer fights, which all fight on battle() (shared/arena.ts) in its 'arena' mode. Never on the hunt,
//   against bosses or the Boss Ladder, or in Guild War. Power (shared/power.ts) leaves them out, like type match-ups.
// - Each boss has its own pool of four. Bosses and the Boss Ladder drop PIECES rarely (CONFIG.held); ten pieces of one
//   item forge it at the crafting bench, and every forge rolls a fresh quality, so forging again to chase a better
//   roll is always worth it. A boss's first clear on the Trail (shared/trail.ts) pays one whole item from its pool.
// - Quality is 50-100% of the item's full effect, top rolls rare (rollHeldQuality). The Spirit Bead's element is
//   chosen when it is forged.
// - Pieces are ordinary bag items (shared/items.ts, kind 'piece'); finished items live in TrainerDTO.held until one
//   is put on a creature (CreatureDTO.held). Both trade on the market; an item comes off its creature before it is
//   listed, and a creature listed, released or offered gives its item back first.
// - An admin switch (kv 'held-on', default on) turns them off everywhere at once: off, every battle is exactly as
//   before held items existed and nothing drops pieces. The server mirrors it into HELD_SWITCH; so does the client.
// - Effects never read board rows, so row items can come later without touching these. On the board some effects are
//   scaled (shared/board.ts BoardRules.heldScale, passed as HeldScale); the item text takes the same scale, so it shows
//   the number that applies.
import { CONFIG } from './config.ts';
import { ELEMENTS, ELEMENT_IDS, type Element } from './elements.ts';
import type { Stats } from './stats.ts';
import type { Rng } from './rng.ts';

/** Live switch, mirrored from the server (kv 'held-on'): battle() reads it through pvpFighter. */
export const HELD_SWITCH = { on: true };

export type HeldEffect = 'hp' | 'guard' | 'will' | 'swift' | 'shield' | 'healKo' | 'reflect' | 'crit' | 'opener' | 'element' | 'rage' | 'drain';
export type HeldBoss = 'fenrir' | 'typhon' | 'hachimata';

export interface HeldDef {
  id: string;
  name: string;
  boss: HeldBoss;
  effect: HeldEffect;
  /** The effect at quality 100: a share (0.08 = 8%) or, for crit, chance points. */
  max: number;
  /** One line of flavour. */
  lore: string;
}

/**
 * The season-one items, four per boss. Numbers tuned in sim/consistency.ts (heldSet rows): a full set of five good
 * rolls against the same line-up without items, at equal Power, wins about 65-75%, never what a 5% Power edge wins.
 * These are the fronts-in-order numbers; the board scales the strongest effects down (shared/board.ts
 * BoardRules.heldScale) so no item worn by all five wins more than about 75% there either.
 */
export const HELD_ITEMS: HeldDef[] = [
  // Fenrir (Midgard): staying power.
  { id: 'hearthstone', name: 'Hearthstone Amulet', boss: 'fenrir', effect: 'hp', max: 0.065, lore: 'A warm stone on a cord, from a hall fire that never went out.' },
  { id: 'wolfhide', name: 'Wolfhide Cloak', boss: 'fenrir', effect: 'guard', max: 0.16, lore: 'Cut from a pelt the great wolf shed in its sleep.' },
  { id: 'wardrune', name: 'Ward Rune', boss: 'fenrir', effect: 'shield', max: 0.065, lore: 'A rune that hardens the air for the first blows.' },
  { id: 'meadhorn', name: 'Mead Horn', boss: 'fenrir', effect: 'healKo', max: 0.085, lore: 'Every victory deserves a drink.' },
  // Typhon (Hellas): tempo and edge.
  { id: 'wingedsandals', name: 'Winged Sandals', boss: 'typhon', effect: 'swift', max: 0.06, lore: 'Light as a messenger and twice as quick.' },
  { id: 'bronzemirror', name: 'Bronze Mirror', boss: 'typhon', effect: 'reflect', max: 0.065, lore: 'Polished until a blow sees itself coming back.' },
  { id: 'harpytalon', name: 'Harpy Talon', boss: 'typhon', effect: 'crit', max: 0.07, lore: 'It always finds the gap in the armour.' },
  { id: 'thunderphial', name: 'Thunder Phial', boss: 'typhon', effect: 'opener', max: 1.7, lore: 'A stoppered storm, opened at the first step.' },
  // Hachimata (Yomi): technique.
  { id: 'spiritbead', name: 'Spirit Bead', boss: 'hachimata', effect: 'element', max: 0.08, lore: 'A prayer bead that hums with one element, chosen at the forge.' },
  { id: 'shimenawa', name: 'Shimenawa Charm', boss: 'hachimata', effect: 'will', max: 0.15, lore: 'A braid of sacred rope that spirits will not cross.' },
  { id: 'onimask', name: 'Oni Mask', boss: 'hachimata', effect: 'rage', max: 0.12, lore: 'The more it bleeds, the wider the grin.' },
  { id: 'foxlantern', name: 'Fox Lantern', boss: 'hachimata', effect: 'drain', max: 0.07, lore: 'Its flame feeds on what it burns.' },
];
export const HELD: Record<string, HeldDef> = Object.fromEntries(HELD_ITEMS.map((d) => [d.id, d]));
export const HELD_BOSSES: readonly HeldBoss[] = ['fenrir', 'typhon', 'hachimata'];
/** A boss's pool, in list order. */
export const heldPool = (boss: string): HeldDef[] => HELD_ITEMS.filter((d) => d.boss === boss);
export const isHeld = (id: unknown): id is string => typeof id === 'string' && Object.hasOwn(HELD, id);

/** dev: from an admin command, never tradable. el: the Spirit Bead's element. */
export interface HeldItem { id: string; item: string; quality: number; el?: Element; dev?: boolean }

/** Pieces per forged item. */
export const PIECES_PER_HELD = 10;
/** The bag item id of an item's pieces. */
export const heldPiece = (item: string) => `${item}-piece`;
/** The held item a piece id belongs to, or null. */
export function pieceOf(itemId: string): string | null {
  const m = /^(.+)-piece$/.exec(itemId);
  return m && isHeld(m[1]) ? m[1] : null;
}

export const QUALITY_MIN = 50;
export const QUALITY_MAX = 100;
/** Top rolls are rare: quality = 50 + ⌊51·u^2.2⌋, so the mean is about 66, 90+ about 1 in 10, 100 about 1 in 110. */
export const QUALITY_EXP = 2.2;
export function rollHeldQuality(u: number): number {
  return Math.min(QUALITY_MAX, QUALITY_MIN + Math.floor((QUALITY_MAX - QUALITY_MIN + 1) * Math.pow(Math.min(Math.max(u, 0), 0.999999), QUALITY_EXP)));
}
/** Chance a forge rolls at least quality q. */
export function qualityAtLeast(q: number): number {
  if (q <= QUALITY_MIN) return 1;
  if (q > QUALITY_MAX) return 0;
  return 1 - Math.pow((q - QUALITY_MIN) / (QUALITY_MAX - QUALITY_MIN + 1), 1 / QUALITY_EXP);
}

/** A new item id: time and a random tail, as relics and shrines. */
export function newHeldId(now = Date.now(), r = Math.random()): string {
  return `h${now.toString(36)}${Math.floor(r * 36 ** 4).toString(36).padStart(4, '0')}`;
}

/** A freshly forged or won item: quality rolled from u; the Spirit Bead takes `el` (else one drawn from u2). */
export function makeHeld(item: string, u: number, opts: { el?: Element; u2?: number; dev?: boolean; now?: number; idR?: number } = {}): HeldItem {
  const def = HELD[item];
  const h: HeldItem = { id: newHeldId(opts.now, opts.idR), item, quality: rollHeldQuality(u) };
  if (def.effect === 'element') h.el = opts.el ?? ELEMENT_IDS[Math.floor((opts.u2 ?? 0) * ELEMENT_IDS.length) % ELEMENT_IDS.length];
  if (opts.dev) h.dev = true;
  return h;
}

/**
 * A scale on held-item effects, by effect: absent = ×1. The board's own (shared/board.ts BoardRules.heldScale) is
 * applied to every board fight by shared/pvpfight.ts; fronts in order never pass one.
 */
export type HeldScale = Partial<Record<HeldEffect, number>>;

/** The effect's size for this item: its full effect times quality, times the fight's scale for its effect. */
export function heldValue(h: Pick<HeldItem, 'item' | 'quality'>, scale?: HeldScale): number {
  const def = HELD[h.item];
  if (!def) return 0;
  const v = (def.max * Math.max(0, Math.min(QUALITY_MAX, h.quality))) / 100;
  const k = scale?.[def.effect];
  return k === undefined ? v : v * k;
}

const pct = (v: number) => `${Math.round(v * 1000) / 10}%`;
/** What the item does, in a few words: "+6.4% HP", "+9% Flame damage". */
export function heldEffectText(h: Pick<HeldItem, 'item' | 'quality' | 'el'>, scale?: HeldScale): string {
  const def = HELD[h.item];
  if (!def) return '';
  const v = heldValue(h, scale);
  switch (def.effect) {
    case 'hp': return `+${pct(v)} HP`;
    case 'guard': return `+${pct(v)} Defense`;
    case 'will': return `+${pct(v)} Resist`;
    case 'swift': return `+${pct(v)} Speed`;
    case 'shield': return `A shield of ${pct(v)} of its HP as it enters the fight`;
    case 'healKo': return `Heals ${pct(v)} of its HP on a knockout`;
    case 'reflect': return `Returns ${pct(v)} of the damage it takes`;
    case 'crit': return `+${pct(v)} critical chance`;
    case 'opener': return `Its first blow deals +${pct(v)} damage`;
    case 'element': return `+${pct(v)} ${h.el ? ELEMENTS[h.el].name : 'element'} damage`;
    case 'rage': return `+${pct(v)} damage below half HP`;
    case 'drain': return `Heals ${pct(v)} of the damage it deals`;
  }
}
/** The item's name, with the Spirit Bead's element: "Flame Spirit Bead". */
export function heldName(h: Pick<HeldItem, 'item' | 'el'>): string {
  const def = HELD[h.item];
  if (!def) return h.item;
  return h.el && def.effect === 'element' ? `${ELEMENTS[h.el].name} ${def.name}` : def.name;
}
/** The effect at quality 100 and at the lowest roll, for the bench and the Help page: "+4-8% HP". */
export function heldRangeText(item: string, scale?: HeldScale): string {
  const lo = heldEffectText({ item, quality: QUALITY_MIN }, scale), hi = heldEffectText({ item, quality: QUALITY_MAX }, scale);
  const a = /([\d.]+)%/.exec(lo)?.[1], b = /([\d.]+)%/.exec(hi)?.[1];
  return a && b ? hi.replace(`${b}%`, `${a}-${b}%`) : hi;
}

// ---------- in a fight ----------
/** What a held item does inside battle() (shared/arena.ts) and the blow formula (shared/combat.ts). */
export interface HeldFx {
  /** Extra critical chance (points). */
  crit?: number;
  /** Moves of this element hit ×elMult. */
  el?: string; elMult?: number;
  /** A shield of this share of max HP each time it steps in. */
  shield?: number;
  /** Heals this share of max HP when its blow knocks out a foe. */
  healKo?: number;
  /** Returns this share of the HP it loses to the attacker. */
  reflect?: number;
  /** Its first blow each time it steps in deals ×(1 + first). */
  first?: number;
  /** All its damage ×(1 + rage) while under half HP. */
  rage?: number;
  /** Heals this share of the HP its blows take. */
  drain?: number;
}

/** Stats with a held item's stat effect (HP, Defense, Resist, Speed); other items leave them as they are. */
export function heldStats(st: Stats, h: Pick<HeldItem, 'item' | 'quality'>, scale?: HeldScale): Stats {
  const def = HELD[h.item];
  if (!def) return st;
  const v = heldValue(h, scale);
  const up = (x: number) => Math.max(1, Math.round(x * (1 + v)));
  switch (def.effect) {
    case 'hp': return { ...st, hp: up(st.hp) };
    case 'guard': return { ...st, guard: up(st.guard) };
    case 'will': return { ...st, will: up(st.will) };
    case 'swift': return { ...st, swift: up(st.swift) };
    default: return st;
  }
}

/** A held item's in-fight effects (none for a stat item, whose effect is in its stats). */
export function heldFx(h: Pick<HeldItem, 'item' | 'quality' | 'el'>, scale?: HeldScale): HeldFx | undefined {
  const def = HELD[h.item];
  if (!def) return undefined;
  const v = heldValue(h, scale);
  switch (def.effect) {
    case 'crit': return { crit: v };
    case 'element': return h.el ? { el: h.el, elMult: 1 + v } : undefined;
    case 'shield': return { shield: v };
    case 'healKo': return { healKo: v };
    case 'reflect': return { reflect: v };
    case 'opener': return { first: v };
    case 'rage': return { rage: v };
    case 'drain': return { drain: v };
    default: return undefined;
  }
}

// ---------- drops ----------
/** Piece drop chance per win (CONFIG.held): boss fights after the Trail first clear, and Boss Ladder wins. */
export const pieceChance = (from: 'boss' | 'ladder') => CONFIG.held.pieceChance[from];

/** One piece roll after a win against `boss`: a piece of a random item from its pool, or null. Draws twice at most. */
export function rollPiece(boss: string, from: 'boss' | 'ladder', rng: Rng): string | null {
  const pool = heldPool(boss);
  if (!pool.length || !rng.chance(pieceChance(from))) return null;
  return heldPiece(rng.pick(pool).id);
}

/** A whole item from a boss's pool (its first clear on the Trail), rolled from the rng. */
export function rollWholeItem(boss: string, rng: Rng, opts: { dev?: boolean; now?: number } = {}): HeldItem | null {
  const pool = heldPool(boss);
  if (!pool.length) return null;
  const def = rng.pick(pool);
  return makeHeld(def.id, rng.next(), { u2: rng.next(), dev: opts.dev, now: opts.now, idR: rng.next() });
}

/** Why `item` can't be forged now (or '' when it can): pieces in the bag and, for the Spirit Bead, an element. */
export function forgeProblem(item: string, bag: Record<string, number>, el?: unknown): string {
  const def = HELD[item];
  if (!def) return 'No such item.';
  if ((bag[heldPiece(item)] ?? 0) < PIECES_PER_HELD) return `${def.name} takes ${PIECES_PER_HELD} pieces.`;
  if (def.effect === 'element' && !(typeof el === 'string' && (ELEMENT_IDS as readonly string[]).includes(el))) return 'Choose an element for the Spirit Bead.';
  return '';
}

/** Valid held item from a message or a save (shape check only; ownership is the server's). */
export function isHeldItem(x: unknown): x is HeldItem {
  const h = x as HeldItem;
  return !!h && typeof h === 'object' && typeof h.id === 'string' && isHeld(h.item) && Number.isFinite(h.quality) && h.quality >= 1 && h.quality <= QUALITY_MAX;
}

// ---------- reading a battle log ----------
type LogBlow = { s: 0 | 1; a: number; d: number; hp: number; ah?: number };
/** Creatures knocked out on each side of a battle log, a Bronze Mirror's return included. */
export function knockouts(log: readonly LogBlow[]): [Set<number>, Set<number>] {
  const down: [Set<number>, Set<number>] = [new Set(), new Set()];
  for (const b of log) {
    if (b.hp <= 0) down[1 - b.s].add(b.d);
    if (b.ah === 0) down[b.s].add(b.a);
  }
  return down;
}
/** HP left per creature at the end of a battle log, from each side's starting HP (held-item heals and returns included). */
export function hpLeft(log: readonly LogBlow[], maxHp: [number[], number[]]): [number[], number[]] {
  const hp: [number[], number[]] = [[...maxHp[0]], [...maxHp[1]]];
  for (const b of log) {
    hp[1 - b.s][b.d] = b.hp;
    if (b.ah !== undefined) hp[b.s][b.a] = b.ah;
  }
  return hp;
}
